import type { Area, Dashboard, Device, DeviceValue, Integration, Room, Scene, Widget } from '../types';
import { widgetConfigOf, type DashboardWidgetLike } from '../components/dashboard/dashboardTypes';
import { wireBoolean, wireJsonValue, wireNumber, wireObject, wireRow, wireString, type WireObject } from './wire';

/**
 * Readers for the hub's JSON rows.
 *
 * The hub types its own models as untyped JSON and a fetch body carries whatever
 * the route returned, so every reader below takes `unknown`, checks it really is
 * a row, normalizes the alias spellings this app has always accepted
 * (`value_id` / `valueId`, `room_name` / `roomName`, …), and hands back a typed
 * row. Keys a reader does not touch pass through exactly as they arrived.
 */

/**
 * The dashboard row as the API returns it: the hub's Dashboard plus embedded widgets.
 *
 * The index signature is what the row really has — the wire carries keys this app
 * never declares (and views read their own subset off it, e.g. the template
 * picker's `DashboardTemplate`) — so it is stated here rather than cast away at
 * each caller.
 */
export interface DashboardResponse extends Dashboard {
  widgets?: Widget[];
  [key: string]: unknown;
}

/**
 * The date behind a wire spelling. ISO strings and epoch numbers both read; a
 * missing value stays an invalid Date, as it always did.
 */
function parseDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date(String(value ?? ''));
}

/** `raw` as a row, or an empty row when the payload is not an object at all. */
function row(raw: unknown): WireObject {
  return wireObject(raw) ?? {};
}

/**
 * Deserializes a device value from API response
 */
export function deserializeDeviceValue(raw: unknown): DeviceValue {
  const r = row(raw);
  return wireRow<DeviceValue>(r, {
    id: wireString(r.id) || wireString(r.valueId) || wireString(r.value_id) || '',
    deviceId: wireString(r.deviceId) || wireString(r.device_id) || '',
    valueKey: wireString(r.valueKey) || wireString(r.value_key) || wireString(r.key) || '',
    currentValue: wireJsonValue(r.currentValue ?? r.current_value ?? r.value),
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
  });
}

/**
 * Deserializes a device from API response
 */
export function deserializeDevice(raw: unknown): Device {
  const r = row(raw);
  const values = r.values ?? r.deviceValues;

  // Don't spread ...raw to avoid property conflicts between values and deviceValues
  return wireRow<Device>(r, {
    id: wireString(r.id) || wireString(r.deviceId) || wireString(r.device_id) || '',
    deviceName: wireString(r.deviceName) || wireString(r.device_name) || wireString(r.name) || '',
    roomId: wireString(r.roomId) || wireString(r.room_id),
    integrationId: wireString(r.integrationId) || wireString(r.integration_id),
    endpointId: wireString(r.endpointId) || wireString(r.endpoint_id),
    endpoint: r.endpoint as Device['endpoint'],
    deviceType: wireString(r.deviceType) || wireString(r.device_type),
    deviceControl: wireString(r.deviceControl) || wireString(r.device_control),
    icon: wireString(r.icon),
    enabled: wireBoolean(r.enabled) ?? true,
    config: wireJsonValue(r.config),
    metadata: wireJsonValue(r.metadata),
    settings: wireJsonValue(r.settings),
    logicScript: wireString(r.logicScript) || wireString(r.logic_script),
    sortOrder: wireNumber(r.sortOrder) ?? wireNumber(r.sort_order) ?? 0,
    shortName: wireString(r.shortName) ?? wireString(r.short_name) ?? null,
    speechFullName: wireString(r.speechFullName) ?? wireString(r.speech_full_name) ?? null,
    speechShortName: wireString(r.speechShortName) ?? wireString(r.speech_short_name) ?? null,
    brandName: wireString(r.brandName) ?? wireString(r.brand_name) ?? null,
    productName: wireString(r.productName) ?? wireString(r.product_name) ?? null,
    modelNumber: wireString(r.modelNumber) ?? wireString(r.model_number) ?? null,
    manualUrl: wireString(r.manualUrl) ?? wireString(r.manual_url) ?? null,
    productUrl: wireString(r.productUrl) ?? wireString(r.product_url) ?? null,
    thumbnailS3Key: wireString(r.thumbnailS3Key) ?? wireString(r.thumbnail_s3_key) ?? undefined,
    thumbnailUrl: wireString(r.thumbnailUrl) ?? wireString(r.thumbnail_url) ?? null,
    stateThumbnailUrl: wireString(r.stateThumbnailUrl) ?? wireString(r.state_thumbnail_url) ?? null,
    deviceValues: Array.isArray(values) ? values.map(deserializeDeviceValue) : undefined,
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
  });
}

/**
 * Deserializes a room from API response
 */
export function deserializeRoom(raw: unknown): Room {
  const r = row(raw);
  return wireRow<Room>(r, {
    id: wireString(r.id) || wireString(r.roomId) || wireString(r.room_id) || '',
    roomName: wireString(r.roomName) || wireString(r.room_name) || wireString(r.name) || '',
    areaId: wireString(r.areaId) ?? wireString(r.area_id) ?? null,
    sortOrder: wireNumber(r.sortOrder) ?? wireNumber(r.sort_order) ?? 0,
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
  });
}

/**
 * Deserializes an area from API response
 */
export function deserializeArea(raw: unknown): Area {
  const r = row(raw);
  return wireRow<Area>(r, {
    id: wireString(r.id) || wireString(r.areaId) || wireString(r.area_id) || '',
    areaName: wireString(r.areaName) || wireString(r.area_name) || wireString(r.name) || '',
    sortOrder: wireNumber(r.sortOrder) ?? wireNumber(r.sort_order) ?? 0,
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
  });
}

/**
 * Deserializes an integration from API response
 */
export function deserializeIntegration(raw: unknown): Integration {
  const r = row(raw);
  return wireRow<Integration>(r, {
    id: wireString(r.id) || wireString(r.integrationId) || wireString(r.integration_id) || '',
    integrationName: wireString(r.integrationName) || wireString(r.integration_name) || wireString(r.name) || '',
    integrationType: wireString(r.integrationType) || wireString(r.integration_type) || wireString(r.type) || '',
    connected: wireBoolean(r.connected) ?? false,
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
    lastConnectedAt:
      r.lastConnectedAt || r.last_connected_at ? parseDate(r.lastConnectedAt || r.last_connected_at) : undefined,
  });
}

/**
 * Deserializes a dashboard from API response
 */
export function deserializeDashboard(raw: unknown): DashboardResponse {
  const r = row(raw);
  return wireRow<DashboardResponse>(r, {
    id: wireString(r.id) || wireString(r.dashboard_id) || wireString(r.dashboardId) || '',
    groupId: wireString(r.groupId) ?? wireString(r.group_id) ?? null,
    isDefault: wireBoolean(r.isDefault) || wireBoolean(r.is_default) || false,
    isTemplate: wireBoolean(r.isTemplate) || wireBoolean(r.is_template) || false,
    templateId: wireString(r.templateId) || wireString(r.template_id),
    templateKey: wireString(r.templateKey) || wireString(r.template_key) || null,
    layoutType: wireString(r.layoutType) || wireString(r.layout_type) || '',
    layoutConfig: wireJsonValue(r.layoutConfig ?? r.layout_config),
    sortOrder: wireNumber(r.sortOrder) ?? wireNumber(r.sort_order) ?? 0,
    widgets: Array.isArray(r.widgets) ? r.widgets.map(deserializeWidget) : undefined,
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
  });
}

/**
 * Deserializes a widget from API response.
 *
 * The row comes back as the dashboard's own read view of it
 * (`DashboardWidgetLike`): the hub `Widget` with the stored config column
 * narrowed to the bag the layouts and the widget editor read, so a row that
 * has been through here never needs its `widgetConfig` narrowed again
 * (card t_9d7cfe72).
 */
export function deserializeWidget(raw: unknown): DashboardWidgetLike {
  const r = row(raw);
  return wireRow<DashboardWidgetLike>(r, {
    id: wireString(r.id) || wireString(r.widget_id) || wireString(r.widgetId) || '',
    layoutId: wireString(r.layoutId) || wireString(r.layout_id) || '',
    widgetType: wireString(r.widgetType) || wireString(r.widget_type) || '',
    widgetConfig: widgetConfigOf(wireJsonValue(r.widgetConfig ?? r.widget_config)),
    positionX: wireNumber(r.positionX) ?? wireNumber(r.position_x) ?? 0,
    positionY: wireNumber(r.positionY) ?? wireNumber(r.position_y) ?? 0,
    colSpan: wireNumber(r.colSpan) ?? wireNumber(r.col_span) ?? 1,
    rowSpan: wireNumber(r.rowSpan) ?? wireNumber(r.row_span) ?? 1,
    sortOrder: wireNumber(r.sortOrder) ?? wireNumber(r.sort_order) ?? 0,
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
  });
}

/**
 * Deserializes a scene from API response
 */
export function deserializeScene(raw: unknown): Scene {
  const r = row(raw);
  return wireRow<Scene>(r, {
    sceneName: wireString(r.sceneName) || wireString(r.scene_name) || wireString(r.name) || '',
    createdAt: parseDate(r.createdAt || r.created_at),
    updatedAt: parseDate(r.updatedAt || r.updated_at),
  });
}
